import { api, type AnyDesign, type DesignMeta, type MakeFor, type MyDesign, type SharedDesign } from '../sync/api';
import { PRESETS, presetPx } from './docSheet';
import { setPressed } from './dom';
import type { Saver } from '../sync/saver';
import { thDateTime } from '../time';
import { $ } from './dom';
import { closeSheets } from './sheets';
import { toast } from './toast';
import { fmtTime } from '../video/project';

/** "1080 × 1080 px" for a design, "วิดีโอ 9:16 · 0:38.0" for a video */
const kindText = (d: DesignMeta) => d.kind === 'video'
  ? `วิดีโอ ${d.width === d.height ? '1:1' : '9:16'} · ${fmtTime((d.durationMs ?? 0) / 1000)}`
  : `${d.width} × ${d.height} px`;

/*
 * "งานของฉัน", tabs:
 *   ของฉัน        my designs on the server: open, share, delete (two taps), new; "แก้โดยผู้ดูแล …" when an admin
 *                 saved it last or made it for me (0015)
 *   แชร์กับฉัน     designs others shared with me: open read-only, or copy as my own
 *   งานของทุกคน   admins only (0015, 1ก 2ข 3ก 5ข): every member's work — search, one member, open read-only then
 *                 "แก้ไขในฐานะผู้ดูแล", what admins did (ประวัติ), delete (two taps + confirm); make a work for a member
 */
/** `videoKeys`/`dropCopies`: a deleted video work's clip copies on this device are deleted too (src/ui/videoStore.ts) */
export function initMineSheet(saver: Saver, opts: {
  openShare: (d: { id: string; name: string }) => void; busy: () => string | null;
  videoKeys: (id: string) => Promise<string[]>; dropCopies: (id: string, keys: string[]) => Promise<void>;
  /** 0015: the signed-in account is an admin (the server checks every request too) */
  isAdmin: () => boolean;
}) {
  const msg = $('mineMsg'), list = $('mineList'), newBtn = $('mineNew') as HTMLButtonElement;
  const newRow = $('mineNewRow');
  const newVideoBtns = [$('mineNewV916'), $('mineNewV11')] as HTMLButtonElement[];
  const tabMine = $('mineTabMine'), tabShared = $('mineTabShared'), tabAll = $('mineTabAll');
  let tab: 'mine' | 'shared' | 'all' = 'mine';
  let armedDelete: string | null = null;
  let armTimer: ReturnType<typeof setTimeout> | undefined;
  let loading = false;
  let counts = { mine: -1, shared: -1, all: -1 };

  const say = (text: string) => { msg.textContent = text; msg.hidden = !text; };
  const errText = (e: unknown) => e instanceof Error ? e.message : String(e);

  /** Don't replace the open work while it has edits that exist only on this device. */
  async function guard(): Promise<boolean> {
    const busy = opts.busy();
    if (busy) { say(`${busy} จึงยังไม่เปลี่ยนงาน`); return false; }
    const why = await saver.flush();
    if (why) { say(`${why} จึงยังไม่เปลี่ยนงาน เพื่อไม่ให้การแก้ไขหาย`); return false; }
    return true;
  }

  function paintTabs() {
    tabMine.setAttribute('aria-selected', String(tab === 'mine'));
    tabShared.setAttribute('aria-selected', String(tab === 'shared'));
    tabAll.setAttribute('aria-selected', String(tab === 'all'));
    tabMine.textContent = `ของฉัน${counts.mine >= 0 ? ` ${counts.mine}` : ''}`;
    tabShared.textContent = `แชร์กับฉัน${counts.shared >= 0 ? ` ${counts.shared}` : ''}`;
    tabAll.textContent = `ทุกคน${counts.all >= 0 ? ` ${counts.all}` : ''}`;
    tabAll.hidden = !opts.isAdmin();
    newRow.hidden = tab !== 'mine';
    $('mineAllBar').hidden = tab !== 'all';
  }
  tabMine.onclick = () => { tab = 'mine'; armedDelete = null; void render(); };
  tabShared.onclick = () => { tab = 'shared'; armedDelete = null; void render(); };
  tabAll.onclick = () => { tab = 'all'; armedDelete = null; void render(); void loadPeople(); };

  function nameButton(name: string, sub: string, current: boolean) {
    const open = document.createElement('button');
    open.className = 'mname';
    const b = document.createElement('b'); b.textContent = name;
    const s = document.createElement('small'); s.textContent = sub;
    open.append(b, s);
    if (current) open.setAttribute('aria-current', 'true');
    return open;
  }
  function smallBtn(text: string, cls: string, label: string) {
    const b = document.createElement('button');
    b.className = cls; b.textContent = text; b.setAttribute('aria-label', label);
    return b;
  }

  /* ---------- ของฉัน ---------- */
  function mineRow(d: MyDesign) {
    const li = document.createElement('li');
    const isOpen = d.id === saver.link.id && !saver.viewing;
    if (isOpen) li.className = 'current';
    const shareText = d.groupShared ? ' · แชร์ทั้งกลุ่ม' : d.shares ? ` · แชร์ ${d.shares} คน` : '';
    // 0015 (4ก): an admin saved it last, or made it for me
    const byAdmin = d.editedBy ? ` · แก้โดยผู้ดูแล ${d.editedBy.name} ${thDateTime(new Date(d.editedBy.at))}`
      : d.madeBy ? ` · ผู้ดูแล ${d.madeBy.name} สร้างให้` : '';
    const open = nameButton(d.name, `${kindText(d)}${(d.pages ?? 1) > 1 ? ` · ${d.pages} หน้า` : ''} · แก้ล่าสุด ${thDateTime(new Date(d.updatedAt))}${shareText}${byAdmin}${isOpen ? ' · เปิดอยู่' : ''}`, isOpen);
    open.onclick = async () => {
      if (isOpen) { closeSheets(); return; }
      if (!(await guard())) return;
      try {
        const res = await api.get(d.id);
        await saver.openDesign(res.design, res.json);
        closeSheets(); toast(`เปิด "${res.design.name}" แล้ว`);
      } catch (e) { say('เปิดงานไม่สำเร็จ: ' + errText(e)); }
    };
    const share = smallBtn('แชร์', 'mbtn', `แชร์งาน ${d.name}`);
    share.onclick = () => opts.openShare({ id: d.id, name: d.name });
    const del = smallBtn(armedDelete === d.id ? 'แตะอีกครั้งเพื่อลบ' : 'ลบ', 'mdel', `ลบงาน ${d.name}`);
    del.onclick = async () => {
      if (armedDelete !== d.id) {
        armedDelete = d.id; clearTimeout(armTimer);
        armTimer = setTimeout(() => { armedDelete = null; void render(); }, 3500);
        void render(); return;
      }
      armedDelete = null;
      if (d.kind === 'video') {
        const busy = opts.busy();
        if (busy) { say(`${busy} จึงยังไม่ลบงาน`); void render(); return; }
      }
      // which clip copies this video uses, read before the work is gone (failure: the work is still deleted, copies stay)
      let keys: string[] = [];
      let keysError = '';
      if (d.kind === 'video') { try { keys = await opts.videoKeys(d.id); } catch (e) { keysError = errText(e); } }
      try {
        await api.remove(d.id);
        if (d.id === saver.link.id && !saver.viewing) await saver.newDesign();
        toast(`ลบ "${d.name}" แล้ว${d.shares ? ' และยกเลิกการแชร์แล้ว' : ''}`);
        if (keysError) toast(`ลบงานแล้ว แต่อ่านรายการคลิปของงานนี้ไม่ได้ (${keysError}) สำเนาคลิปยังอยู่ในเครื่อง ล้างได้ที่ เพิ่มเติม › พื้นที่วิดีโอในเครื่อง`, true);
        else if (keys.length) await opts.dropCopies(d.id, keys);
      } catch (e) { say('ลบงานไม่สำเร็จ: ' + errText(e)); }
      void render();
    };
    li.append(open, share, del);
    return li;
  }

  /* ---------- แชร์กับฉัน ---------- */
  function sharedRow(d: SharedDesign) {
    const li = document.createElement('li');
    const isOpen = saver.viewing?.id === d.id;
    if (isOpen) li.className = 'current';
    const who = (d.owner.name || d.owner.email) + (d.owner.status === 'disabled' ? ' (ปิดการใช้งาน)' : '');
    const open = nameButton(d.name, `ของ ${who}${d.kind === 'video' ? ` · ${kindText(d)}` : ''}${(d.pages ?? 1) > 1 ? ` · ${d.pages} หน้า` : ''} · ${d.via === 'group' ? 'ทั้งกลุ่ม' : 'แชร์ให้คุณ'} · แก้ล่าสุด ${thDateTime(new Date(d.updatedAt))}${isOpen ? ' · เปิดอยู่' : ''}`, isOpen);
    open.onclick = async () => {
      if (isOpen) { closeSheets(); return; }
      if (!(await guard())) return;
      try {
        const res = await api.get(d.id);
        if (res.access !== 'view' || !res.owner) { await saver.openDesign(res.design, res.json); closeSheets(); return; }
        await saver.enterView(res.design, res.json, res.owner);
        closeSheets(); toast(`เปิด "${res.design.name}" แบบดูอย่างเดียว`);
      } catch (e) { say('เปิดงานไม่สำเร็จ: ' + errText(e)); void render(); }
    };
    const copy = smallBtn('ทำสำเนา', 'mbtn', `ทำสำเนา ${d.name} เป็นงานของฉัน`);
    copy.onclick = async () => {
      copy.disabled = true;
      try {
        const res = await api.copy(d.id);
        toast(`ทำสำเนาแล้ว ชื่อ "${res.design.name}" อยู่ในแท็บ ของฉัน`);
        tab = 'mine';
      } catch (e) { say('ทำสำเนาไม่สำเร็จ: ' + errText(e)); }
      copy.disabled = false;
      void render();
    };
    li.append(open, copy);
    return li;
  }

  /* ---------- งานของทุกคน (admins, 0015) ---------- */
  let openHistory: string | null = null;
  function allRow(d: AnyDesign): HTMLLIElement[] {
    const li = document.createElement('li');
    const isOpen = d.id === saver.link.id && !saver.viewing || saver.viewing?.id === d.id;
    if (isOpen) li.className = 'current';
    const who = d.mine ? 'งานของคุณ' : `ของ ${d.owner.name || d.owner.email}${d.owner.status === 'disabled' ? ' (ปิดการใช้งาน)' : ''}`;
    const by = d.editedBy ? ` · แก้โดยผู้ดูแล ${d.editedBy.name}` : '';
    const open = nameButton(d.name, `${who} · ${kindText(d)}${(d.pages ?? 1) > 1 ? ` · ${d.pages} หน้า` : ''} · แก้ล่าสุด ${thDateTime(new Date(d.updatedAt))}${by}${isOpen ? ' · เปิดอยู่' : ''}`, isOpen);
    open.onclick = async () => {
      if (isOpen) { closeSheets(); return; }
      if (!(await guard())) return;
      try {
        const res = await api.get(d.id);
        if (res.access === 'owner' || !res.owner) { await saver.openDesign(res.design, res.json); closeSheets(); toast(`เปิด "${res.design.name}" แล้ว`); return; }
        // 2ข: read-only first; "แก้ไขในฐานะผู้ดูแล" on the bar turns editing on
        await saver.enterView(res.design, res.json, res.owner, res.access === 'admin');
        closeSheets(); toast(`เปิดงานของ ${res.owner.name || res.owner.email} แบบดูอย่างเดียว กด "แก้ไขในฐานะผู้ดูแล" เพื่อแก้`);
      } catch (e) { say('เปิดงานไม่สำเร็จ: ' + errText(e)); void render(); }
    };
    const hist = smallBtn(openHistory === d.id ? 'ซ่อนประวัติ' : 'ประวัติ', 'mbtn', `ประวัติการแก้ของผู้ดูแล ${d.name}`);
    hist.onclick = () => { openHistory = openHistory === d.id ? null : d.id; void render(); };
    const del = smallBtn(armedDelete === d.id ? 'แตะอีกครั้งเพื่อลบ' : 'ลบ', 'mdel', `ลบงาน ${d.name}`);
    del.onclick = async () => {
      if (armedDelete !== d.id) {
        armedDelete = d.id; clearTimeout(armTimer);
        armTimer = setTimeout(() => { armedDelete = null; void render(); }, 3500);
        void render(); return;
      }
      armedDelete = null;
      // 5ข: a member's work needs a second, explicit "yes" that names whose it is
      if (!d.mine && !confirm(`ลบงาน "${d.name}" ของ ${d.owner.name || d.owner.email}?\n\nเจ้าของจะไม่เห็นงานนี้อีก (ประวัติการลบเก็บไว้)`)) { void render(); return; }
      if (d.kind === 'video' && d.mine) { const busy = opts.busy(); if (busy) { say(`${busy} จึงยังไม่ลบงาน`); void render(); return; } }
      try {
        await api.remove(d.id);
        if (d.id === saver.link.id && !saver.viewing) await saver.newDesign();
        else if (saver.viewing?.id === d.id) await saver.exitView();
        toast(`ลบ "${d.name}"${d.mine ? '' : ` ของ ${d.owner.name || d.owner.email}`} แล้ว`);
      } catch (e) { say('ลบงานไม่สำเร็จ: ' + errText(e)); }
      void render();
    };
    li.append(open, hist, del);
    if (openHistory !== d.id) return [li];
    const h = document.createElement('li');
    h.className = 'mhist'; h.textContent = 'กำลังโหลดประวัติ…';
    api.history(d.id).then(({ history }) => {
      const what = { create: 'สร้างงานนี้ให้', edit: 'แก้', delete: 'ลบ' } as const;
      h.textContent = history.length ? '' : 'ยังไม่มีผู้ดูแลแก้งานนี้ (การแก้ของเจ้าของเองไม่อยู่ในประวัตินี้)';
      for (const x of history) {
        const line = document.createElement('div');
        const span = x.action === 'edit' && x.firstAt !== x.at ? `${thDateTime(new Date(x.firstAt))} – ${thDateTime(new Date(x.at))}` : thDateTime(new Date(x.at));
        line.textContent = `${span} · ${x.by} ${what[x.action]}${x.version ? ` (เวอร์ชัน ${x.version})` : ''}`;
        h.append(line);
      }
    }, e => { h.textContent = 'โหลดประวัติไม่สำเร็จ: ' + errText(e); });
    return [li, h];
  }

  /* members for the filter and for "สร้างงานให้สมาชิก" (active only: the server refuses the others with the reason) */
  const qBox = $('mineAllQ') as HTMLInputElement, whoBox = $('mineAllWho') as HTMLSelectElement;
  let qTimer: ReturnType<typeof setTimeout> | undefined;
  qBox.oninput = () => { clearTimeout(qTimer); qTimer = setTimeout(() => void render(), 300); };
  whoBox.onchange = () => void render();
  async function loadPeople() {
    try {
      const { users } = await api.admin.users();
      const active = users.filter(u => u.status === 'active').sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, 'th'));
      const fill = (sel: HTMLSelectElement, first: string, withMe: boolean) => {
        const keep = sel.value;
        sel.textContent = '';
        if (first) sel.append(new Option(first, ''));
        for (const u of active) if (withMe || u.email !== saver.email) sel.append(new Option(`${u.name || u.email.split('@')[0]} · ${u.email}`, u.id));
        if ([...sel.options].some(o => o.value === keep)) sel.value = keep;
      };
      // making work "for" myself is just a new work of mine: only other members are offered
      fill(whoBox, 'ทุกคน', true); fill($('mkWho') as HTMLSelectElement, '— เลือกสมาชิก —', false);
    } catch (e) { say('โหลดรายชื่อสมาชิกไม่สำเร็จ: ' + errText(e)); }
  }

  /* ---------- สร้างงานให้สมาชิก (3ก): the member owns it from the start; it opens for editing ---------- */
  let makeFrom: MakeFor['from'] = 'blank';
  const mkSize = $('mkSize') as HTMLSelectElement, mkTpl = $('mkTpl') as HTMLSelectElement, mkCopy = $('mkCopy') as HTMLSelectElement;
  PRESETS.forEach((p, k) => mkSize.append(new Option(p.label, String(k))));
  const mkErr = (t: string) => { $('mkErr').textContent = t; $('mkErr').hidden = !t; };
  async function paintMake() {
    setPressed('mkFrom', b => b.dataset.v === makeFrom);
    mkSize.hidden = makeFrom !== 'blank'; mkTpl.hidden = makeFrom !== 'template'; mkCopy.hidden = makeFrom !== 'copy';
    mkErr('');
    try {
      if (makeFrom === 'template' && !mkTpl.options.length) {
        const { templates } = await api.templates();
        templates.forEach(t => mkTpl.append(new Option(`${t.name}${t.kind === 'video' ? ' (วิดีโอ)' : ''}`, t.id)));
        if (!templates.length) mkErr('ยังไม่มีเทมเพลต');
      }
      if (makeFrom === 'copy') {
        const { designs } = await api.list();
        mkCopy.textContent = '';
        designs.forEach(d => mkCopy.append(new Option(d.name, d.id)));
        if (!designs.length) mkErr('คุณยังไม่มีงานบนเซิร์ฟเวอร์ที่จะทำสำเนา');
      }
    } catch (e) { mkErr('โหลดรายการไม่สำเร็จ: ' + errText(e)); }
  }
  $('mkFrom').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    makeFrom = b.dataset.v as MakeFor['from']; void paintMake();
  };
  $('mineMake').addEventListener('toggle', () => { if (($('mineMake') as HTMLDetailsElement).open) { void loadPeople(); void paintMake(); } });
  $('mkGo').onclick = async () => {
    const ownerId = ($('mkWho') as HTMLSelectElement).value;
    if (!ownerId) { mkErr('ยังไม่ได้เลือกสมาชิก'); return; }
    let start: MakeFor;
    const name = ($('mkName') as HTMLInputElement).value.trim();
    if (makeFrom === 'blank') {
      const p = PRESETS[Number(mkSize.value)] ?? PRESETS[0], sz = presetPx(p);
      const w = Math.round(sz.w), h = Math.round(sz.h);
      start = { from: 'blank', design: { name: name || 'งานไม่มีชื่อ', width: w, height: h, bg: '#ffffff', json: JSON.stringify({ version: '5.3.0', objects: [], size: { w, h } }) } };
    } else if (makeFrom === 'template') {
      if (!mkTpl.value) { mkErr('ยังไม่ได้เลือกเทมเพลต'); return; }
      start = { from: 'template', templateId: mkTpl.value };
    } else {
      if (!mkCopy.value) { mkErr('ยังไม่ได้เลือกงานที่จะทำสำเนา'); return; }
      start = { from: 'copy', designId: mkCopy.value };
    }
    if (!(await guard())) return;
    const btn = $('mkGo') as HTMLButtonElement;
    btn.disabled = true; mkErr('');
    try {
      const made = await api.admin.makeFor(ownerId, start, name);
      const res = await api.get(made.design.id);
      await saver.openAsAdmin(res.design, res.json, res.owner ?? made.owner);
      ($('mkName') as HTMLInputElement).value = '';
      closeSheets();
      toast(`สร้าง "${made.design.name}" ให้ ${made.owner.name || made.owner.email} แล้ว แก้ต่อได้เลย ทุกการแก้บันทึกลงงานของเจ้าของ`);
    } catch (e) { mkErr('สร้างงานไม่สำเร็จ: ' + errText(e)); }
    finally { btn.disabled = false; }
  };

  async function render() {
    if (loading) return;
    if (tab === 'all' && !opts.isAdmin()) tab = 'mine';
    paintTabs();
    $('mineWho').textContent = saver.mode === 'online'
      ? (tab === 'mine' ? `งานของ ${saver.email} (คนอื่นเห็นเฉพาะงานที่คุณแชร์)`
        : tab === 'shared' ? 'งานที่คนอื่นแชร์ให้ ดูได้อย่างเดียว ทำสำเนาเพื่อแก้ไข'
        : 'งานของทุกคน (เฉพาะผู้ดูแล) เปิดแล้วเป็นแบบดูอย่างเดียวก่อน กด "แก้ไขในฐานะผู้ดูแล" เพื่อแก้ งานที่แก้บันทึกลงงานของเจ้าของ')
      : '';
    if (saver.mode !== 'online') {
      list.textContent = '';
      newBtn.disabled = true; newVideoBtns.forEach(b => { b.disabled = true; });
      say(`ยังดูรายการงานไม่ได้: ${saver.reason} ระหว่างนี้งานที่เปิดอยู่บันทึกไว้ในเครื่องนี้`);
      return;
    }
    newBtn.disabled = false; newVideoBtns.forEach(b => { b.disabled = false; });
    loading = true;
    try {
      if (tab === 'mine') {
        const { designs } = await api.list();
        counts.mine = designs.length;
        list.textContent = '';
        say(designs.length ? '' : 'ยังไม่มีงานบนเซิร์ฟเวอร์ งานที่เปิดอยู่จะขึ้นไปเองเมื่อแก้ไข');
        designs.forEach(d => list.appendChild(mineRow(d)));
      } else if (tab === 'all') {
        const { designs } = await api.admin.designs(qBox.value.trim(), whoBox.value);
        counts.all = designs.length;
        list.textContent = '';
        say(designs.length ? '' : qBox.value.trim() || whoBox.value ? 'ไม่พบงานที่ตรงกับที่ค้นหา' : 'ยังไม่มีงานบนเซิร์ฟเวอร์');
        designs.forEach(d => list.append(...allRow(d)));
      } else {
        const { designs } = await api.shared();
        counts.shared = designs.length;
        list.textContent = '';
        say(designs.length ? '' : 'ยังไม่มีงานที่คนอื่นแชร์ให้คุณ');
        designs.forEach(d => list.appendChild(sharedRow(d)));
      }
      paintTabs();
    } catch (e) {
      say('โหลดรายการงานไม่สำเร็จ: ' + errText(e));
    } finally { loading = false; }
  }

  newBtn.onclick = async () => {
    if (!(await guard())) return;
    await saver.newDesign();
    closeSheets(); toast('เริ่มงานใหม่แล้ว');
  };
  /* a new video project: portrait for Reels/TikTok or square (src/video/project.ts) */
  ([[newVideoBtns[0], '9:16'], [newVideoBtns[1], '1:1']] as const).forEach(([b, frame]) => {
    b.onclick = async () => {
      if (!(await guard())) return;
      await saver.newVideo(frame);
      closeSheets(); toast(`เริ่มงานวิดีโอ ${frame} แล้ว กด "+ คลิป" เพื่อเลือกวิดีโอจากเครื่อง`);
    };
  });

  return { render, showShared: () => { tab = 'shared'; } };
}
